import React from 'react';
import { M } from '@dashboard-experience/mastodon';
import T from 'components/T';
import { moment, i18n } from '@international/mastodon-i18n';
import EmploymentIcon from '../../../images/icons/documents/Employment.svg?react';
import { readPolicy, findPinnedDocument } from './utils/formatter';
import SimplePanel from '../../Panels/SimplePanel';
import SimpleDocumentList from '../../fields/SimpleDocumentList';
import UploadButton from '../../fields/UploadButton';
import secureFolderIcon from '../../../images/icons/documents/SecureFolder.svg';
import { CandidateContextEditor } from './CandidateContext';
import {
  CircleParameters,
  CircleProps,
  DateComponentProps,
  DocumentListSectionProps,
  DocumentUploadHeaderProps,
  GenerateDateComponentsProps,
  ModalFooterProps,
  PreviouslyProvided,
} from './utils/interfaces';

const DEFAULT_CIRCLE_PARAMETERS: CircleParameters = {
  cx: 75,
  cy: 75,
  radius: 60,
  strokeWidth: 15,
  stroke: '#E1E6EB',
};

const PROGRESS_CIRCLE_COLOR = '#009CB8';

const Circle: React.FC<CircleProps> = ({ circleParameters, progress = 0 }) => {
  const { cx, cy, radius, strokeWidth, stroke } = circleParameters;
  const circumference = 2 * Math.PI * radius;
  const offset = (1 - progress) * circumference;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      stroke={stroke}
      strokeWidth={strokeWidth}
      fill='none'
      strokeDasharray={circumference}
      strokeDashoffset={offset}
      strokeLinecap='round'
      transform='rotate(-90 75 75)'
    />
  );
};

const DateComponent: React.FC<DateComponentProps> = ({
  date,
  keyName,
  uploaded,
  document = [],
  onRemoveByUploadId,
  boxed = false,
  row: rowData,
  model,
  candidateDocContextEnabled = false,
  onRowUploadSuccess,
  onSaveContext,
  onContextEditingChange,
  pinnedDocument,
  shouldSubmit,
}) => {
  // Boxed (on-flag) rows use a muted grey document icon that matches the
  // adjacent label; the legacy inline rows keep the blue icon.
  let iconClassName = 'checkr-blue';
  if (uploaded) {
    iconClassName = 'status-clear';
  } else if (boxed) {
    iconClassName = 'multidoc-doc-row__icon';
  }

  const slotLabel = i18n.getStr(
    `components.Verification.EmploymentMultidocPanel.${keyName}`,
    { date },
  );

  // EVERI-3323: on-flag, a satisfied required slot renders the locked-design
  // "submitted" row — green check, the uploaded filename as the primary line,
  // a "Submitted · {slot}" subline, and a right-aligned remove control. The
  // empty-slot and off-flag rows keep their existing treatment below.
  // EVERI-4005: a pinned row renders identically (no visual distinction from a genuine match, matching apply's DocumentSlot).
  const filledDocument = uploaded && document.length > 0 ? document[0] : null;
  const rowDocument = filledDocument || pinnedDocument;
  if (boxed && rowDocument) {
    const pinnedContext = pinnedDocument
      ? (pinnedDocument as any)?.metadata?.context_attributes
          ?.candidate_context ?? ''
      : '';
    return (
      <div className='multidoc-doc-row multidoc-doc-row--submitted'>
        <span className='multidoc-doc-row__check'>
          <M.Icon icon='CheckmarkFilled' />
        </span>
        <div className='multidoc-doc-row__body'>
          <span className='multidoc-doc-row__filename'>
            {rowDocument.filename}
          </span>
          <span className='multidoc-doc-row__status'>
            <span className='multidoc-doc-row__submitted'>
              {i18n.getStr(
                'components.Verification.EmploymentMultidocPanel.submittedLabel',
              )}
            </span>
            {` · ${slotLabel}`}
          </span>
          {pinnedDocument && candidateDocContextEnabled && onSaveContext && (
            <CandidateContextEditor
              id={`candidate-context-${pinnedDocument.uploadId}`}
              value={pinnedContext}
              onSave={value =>
                onSaveContext(
                  pinnedDocument.uploadId,
                  pinnedDocument.exceptionId,
                  value,
                )
              }
              onEditingChange={onContextEditingChange}
            />
          )}
        </div>
        <button
          type='button'
          className='multidoc-doc-row__remove'
          aria-label={i18n.getStr('buttons.remove')}
          onClick={() =>
            onRemoveByUploadId?.(rowDocument.uploadId, rowDocument.exceptionId)
          }
        >
          <M.Icon icon='Close' />
        </button>
      </div>
    );
  }

  // EVERI-4005: empty required row gets its own single-file upload button (on-flag only).
  if (boxed && !uploaded && candidateDocContextEnabled && rowData && model) {
    return (
      <div className='multidoc-doc-row multidoc-doc-row--empty'>
        <M.Icon
          style={{ marginRight: 8 }}
          icon='Document'
          className='multidoc-doc-row__icon'
          size={16}
        />
        &nbsp;
        <span className='text-panel'>{slotLabel}</span>
        <div className='multidoc-doc-row__upload'>
          <UploadButton
            kind='secondary'
            name={`${model}-row-${rowData.id}`}
            hideIcon
            buttonText='buttons.uploadDocument'
            buttonClass=''
            context='multi-documents'
            fileOptions={{ maxFiles: 1 }}
            handleUploadSuccess={(res: { filesUploaded: any[] }) =>
              onRowUploadSuccess?.(res, rowData)
            }
            shouldSubmit={shouldSubmit}
          />
        </div>
      </div>
    );
  }

  const row = (
    <>
      <M.Icon
        style={{ marginRight: boxed ? 8 : 0 }}
        icon={uploaded ? 'CheckmarkFilled' : 'Document'}
        className={iconClassName}
        size={boxed ? 16 : undefined}
      />
      &nbsp;
      <span className='text-panel'>{slotLabel}</span>
      {document.length > 0 && (
        <div style={{ marginLeft: '24px' }}>
          <SimpleDocumentList
            onRemoveByUploadId={onRemoveByUploadId}
            documents={document}
            useTagStyle
          />
        </div>
      )}
    </>
  );

  // EVERI-3323: on-flag, each requested-document slot renders as a bordered box
  // (per the locked design) instead of the legacy inline list with <br/>s.
  if (boxed) {
    return <div className='multidoc-doc-row'>{row}</div>;
  }

  return (
    <>
      {row}
      <br />
      <br />
    </>
  );
};

const DocumentListSection: React.FC<DocumentListSectionProps> = ({
  additionalDocuments,
  onRemoveByUploadId,
}) => (
  <SimplePanel>
    <div className='spaced-items'>
      <M.GridRow className='flex-center'>
        <M.GridCol md={12} className='no-padding'>
          <img src={secureFolderIcon} alt='Employment Proof' />
        </M.GridCol>
        <M.GridCol>
          <DocumentSectionTitle
            titleKey='components.employment.uploadedDocuments'
            reviewKey=''
          />
        </M.GridCol>
      </M.GridRow>
      {additionalDocuments.length > 0 && (
        <>
          <M.GridRow>
            <M.GridCol className='padded-left'>
              <DocumentSectionTitle
                titleKey='components.employment.otherDocuments'
                reviewKey='components.employment.otherDocumentsReview'
                headingLevel='h3'
              />
            </M.GridCol>
          </M.GridRow>
          <M.GridRow>
            <M.GridCol className='padded-left'>
              <SimpleDocumentList
                onRemoveByUploadId={onRemoveByUploadId}
                documents={additionalDocuments}
                useTagStyle
              />
            </M.GridCol>
          </M.GridRow>
        </>
      )}
    </div>
  </SimplePanel>
);

// EVERI-3323: on-flag "Other documents" block rendered inside the upload card
// (per the locked mock) when a candidate has uploaded documents that didn't
// satisfy a required slot. Unlike the off-flag DocumentListSection, this has no
// "Uploaded documents" header/folder icon — just a divider, the "Other
// documents" heading + review copy, and the uploaded tags.
const OtherDocumentsSection: React.FC<{
  additionalDocuments: DocumentListSectionProps['additionalDocuments'];
  onRemoveByUploadId: DocumentListSectionProps['onRemoveByUploadId'];
  candidateDocContextEnabled?: boolean;
  onSaveContext?: (
    uploadId: string,
    exceptionId: string | undefined,
    value: string,
  ) => void;
  onContextEditingChange?: (isEditing: boolean) => void;
}> = ({
  additionalDocuments,
  onRemoveByUploadId,
  candidateDocContextEnabled,
  onSaveContext,
  onContextEditingChange,
}) => (
  <div className='multidoc-other-documents'>
    <hr className='multidoc-other-documents__divider' />
    <DocumentSectionTitle
      titleKey='components.employment.otherDocuments'
      reviewKey='components.employment.otherDocumentsReviewV2'
      headingLevel='h3'
    />
    <div className='multidoc-other-documents__list'>
      {candidateDocContextEnabled && onSaveContext ? (
        // EVERI-4005: apply parity — each "other" doc gets its own bordered row + Add/Edit context, not just a removable tag.
        additionalDocuments.map((doc: any) => (
          <div
            key={doc.uploadId}
            className='multidoc-doc-row multidoc-doc-row--other'
          >
            <M.Icon
              style={{ marginRight: 8 }}
              icon='Document'
              className='multidoc-doc-row__icon'
              size={16}
            />
            <div className='multidoc-doc-row__body'>
              <span className='multidoc-doc-row__filename'>{doc.filename}</span>
              <CandidateContextEditor
                id={`candidate-context-${doc.uploadId}`}
                value={
                  doc?.metadata?.context_attributes?.candidate_context ?? ''
                }
                onSave={value =>
                  onSaveContext(doc.uploadId, doc.exceptionId, value)
                }
                onEditingChange={onContextEditingChange}
              />
            </div>
            <button
              type='button'
              className='multidoc-doc-row__remove'
              aria-label={i18n.getStr('buttons.remove')}
              onClick={() => onRemoveByUploadId(doc.uploadId, doc.exceptionId)}
            >
              <M.Icon icon='Close' />
            </button>
          </div>
        ))
      ) : (
        <SimpleDocumentList
          onRemoveByUploadId={onRemoveByUploadId}
          documents={additionalDocuments}
          useTagStyle
        />
      )}
    </div>
  </div>
);

// Read-only "What you previously provided" block. Surfaces the apply-time
// supporting documents + explanation the candidate submitted on their original
// background check, beneath the outstanding required docs on the exception page
// (per the locked mock). The docs render as read-only tags (no remove control)
// and the explanation is a read-only box, mirroring apply's saved-entry
// treatment. Returns null when there's nothing to show so no empty header
// renders — the graceful no-render that keeps the page unchanged until the BE
// payload ships and for candidates who provided nothing at apply time.
const PreviouslyProvidedSection: React.FC<{
  previouslyProvided?: PreviouslyProvided;
}> = ({ previouslyProvided }) => {
  if (!previouslyProvided) return null;
  const {
    candidateExplanation = '',
    supportingDocuments = [],
    originalReportDateFormatted = '',
  } = previouslyProvided;
  const hasDocuments = supportingDocuments.length > 0;
  if (!hasDocuments && !candidateExplanation) return null;

  return (
    <div className='multidoc-previously-provided'>
      <hr className='multidoc-previously-provided__divider' />
      <h3>
        <T
          value='components.Verification.EmploymentMultidocPanel.whatYouPreviouslyProvided'
          className='text-panel'
        />
      </h3>
      {originalReportDateFormatted && (
        <T
          value='components.Verification.EmploymentMultidocPanel.originalBackgroundCheckDate'
          date={originalReportDateFormatted}
          className='text-panel multidoc-previously-provided__date'
        />
      )}
      {hasDocuments && (
        <div className='multidoc-previously-provided__group'>
          <div className='multidoc-previously-provided__label'>
            <T value='components.Verification.EmploymentMultidocPanel.otherDocumentsYouProvided' />
          </div>
          <div className='multidoc-previously-provided__list'>
            <SimpleDocumentList
              documents={supportingDocuments}
              useTagStyle
              readOnly
            />
          </div>
        </div>
      )}
      {candidateExplanation && (
        <div className='multidoc-previously-provided__group'>
          <div className='multidoc-previously-provided__label'>
            <T value='components.Verification.EmploymentMultidocPanel.explanationYouProvided' />
          </div>
          <div className='multidoc-previously-provided__explanation'>
            {candidateExplanation}
          </div>
        </div>
      )}
    </div>
  );
};

const DocumentUploadHeader: React.FC<DocumentUploadHeaderProps> = ({
  position,
  company,
  startDate,
  endDate,
}) => (
  <div className='text-center document-upload-header'>
    <h4 className='job-title'>
      <span className='company-position'>
        <span className='company'>{company}</span>
        <span className='position'>{position}</span>
      </span>
    </h4>
    <h5 className='text-muted text-bold'>{`${startDate} - ${endDate}`}</h5>
  </div>
);

const GenerateDateComponents: React.FC<GenerateDateComponentsProps> = ({
  documentsRequired,
  policy,
  documents = [],
  onRemoveByUploadId,
  isYearly,
  startDate,
  boxed = false,
  uniformLabel = false,
  model,
  candidateDocContextEnabled = false,
  onRowUploadSuccess,
  onSaveContext,
  onContextEditingChange,
  slotPins = {},
  additionalDocuments = [],
  shouldSubmit,
}) => {
  return (
    <>
      {documentsRequired.map((year: any) => {
        const isStartYear =
          `${moment.utc(startDate, 'MM/YYYY').year()}` === year.value;
        const document = documents
          .filter(doc => {
            if (year.uploadId && doc.uploadId === year.uploadId) {
              return true;
            }
            return false;
          })
          .map(doc => ({
            ...doc,
            exceptionId: year.id,
          }));

        // EVERI-4005: only checked when this row isn't a genuine match; rendering-only.
        const pinnedDocument = year.disabled
          ? undefined
          : findPinnedDocument(year.value, additionalDocuments, slotPins);

        return (
          <DateComponent
            key={`document-${year.value}`}
            date={year.value}
            keyName={
              uniformLabel
                ? 'employmentDocument'
                : readPolicy(policy, isYearly, isStartYear)
            }
            uploaded={year.disabled}
            document={document}
            onRemoveByUploadId={onRemoveByUploadId}
            boxed={boxed}
            row={year}
            model={model}
            candidateDocContextEnabled={candidateDocContextEnabled}
            onRowUploadSuccess={onRowUploadSuccess}
            onSaveContext={onSaveContext}
            onContextEditingChange={onContextEditingChange}
            pinnedDocument={pinnedDocument}
            shouldSubmit={shouldSubmit}
          />
        );
      })}
    </>
  );
};

const generateDateComponentsToString = (
  documentsRequired: any[],
  policy: any,
  isYearly: boolean,
  startDate: string,
) => {
  return documentsRequired
    .reduce((acc, year) => {
      if (year.disabled) return acc;
      const isStartYear =
        `${moment.utc(startDate, 'MM/YYYY').year()}` === year.value;
      const template = i18n.getStr(
        `components.Verification.EmploymentMultidocPanel.${readPolicy(
          policy,
          isYearly,
          isStartYear,
        )}`,
        { date: year.value },
      );
      acc.push(`<li style="margin-left: 1em;">&nbsp;${template}</li>`);
      return acc;
    }, [] as string[])
    .join('\n');
};

const DocumentSectionTitle: React.FC<{
  titleKey: string;
  reviewKey?: string;
  headingLevel?: 'h2' | 'h3';
  reviewContent?: string;
}> = ({ titleKey, reviewKey, headingLevel = 'h2', reviewContent }) => {
  const HeadingTag = headingLevel;
  return (
    <>
      <HeadingTag>
        <T value={titleKey} className='text-panel' dangerousHTML />
      </HeadingTag>
      {reviewKey && (
        <T
          value={reviewKey}
          reviewContent={reviewContent}
          className='text-panel'
          dangerousHTML
        />
      )}
    </>
  );
};

const ModalHeader: React.FC<ModalFooterProps> = ({ onCancel, textContent }) => (
  <div className='cp-modal-header'>
    <div id='left-column'>
      <h2>{textContent}</h2>
    </div>
    <div id='right-column'>
      <button onClick={onCancel} className='close-button' type='button'>
        &times;
      </button>
    </div>
  </div>
);

const ModalFooter: React.FC<ModalFooterProps> = ({
  onCancel,
  handleSubmit,
  button = 'buttons.confirm',
  cancelButton = 'buttons.cancel',
  isGrayed,
  submitDisabled = false,
}) => (
  <div className='cp-modal-footer'>
    <M.Button onClick={onCancel} kind='secondary'>
      {i18n.getStr(cancelButton)}
    </M.Button>
    <M.Button
      className={`${isGrayed ? 'gray-button' : ''}`}
      onClick={handleSubmit}
      kind='primary'
      disabled={submitDisabled}
    >
      {i18n.getStr(button)}
    </M.Button>
  </div>
);

const UploadCircleWrapper: React.FC<{ progress: number }> = ({ progress }) => {
  return (
    <div className='circle-wrapper' aria-label='progress circle'>
      <svg width='150' height='150'>
        <Circle circleParameters={DEFAULT_CIRCLE_PARAMETERS} progress={1} />
        <Circle
          circleParameters={{
            ...DEFAULT_CIRCLE_PARAMETERS,
            stroke: PROGRESS_CIRCLE_COLOR,
          }}
          progress={progress}
        />
      </svg>
      <EmploymentIcon
        aria-label='Employment Proof'
        className='centered-image'
      />
    </div>
  );
};

export {
  Circle,
  DateComponent,
  DocumentListSection,
  OtherDocumentsSection,
  PreviouslyProvidedSection,
  DocumentSectionTitle,
  DocumentUploadHeader,
  GenerateDateComponents,
  generateDateComponentsToString,
  ModalFooter,
  ModalHeader,
  UploadCircleWrapper,
};
